import { Note } from "0.Mine/0.Components/CRM/CustomerDB/NotesDialogV2";
import { ClientsResponse } from "0.Mine/3.Interfaces/Client";

import { AdditionalParams, QlickupActions, useQlickupAPI } from "../APICall";

interface GetClientsParams {
  page?: number;
  limit?: number;
  search?: string;
  orderby?: string;
  order?: "ASC" | "DESC";
  name?: string;
  email?: string;
  phone?: string;
  source?: string;
  customer_group?: string;
  total_spent_min?: number;
  total_spent_max?: number;
  created_from?: string;
  created_to?: string;
  updated_from?: string;
  updated_to?: string;
  last_login_from?: string;
  last_login_to?: string;
  tag?: string;
}

export const useHandleClients = () => {
  const { QlickUpAPI } = useQlickupAPI();

  const bulkUpdateCustomerColumns = async (
    businessId: number,
    preferences: Record<string, string>,
    fieldTypes: Record<string, { type: string; options?: string[] }>
  ) => {
    const additionalParams: AdditionalParams = {
      action: "bulkUpdateCustomerPreferences",
      preferences: JSON.stringify(preferences),
      field_types: JSON.stringify(fieldTypes),
    };

    try {
      const response = await QlickUpAPI(
        businessId.toString(),
        "bulkUpdateCustomerPreferences",
        {
          preferences,
          field_types: fieldTypes,
        },
        "clients",
        QlickupActions.POST,
        additionalParams
      );

      console.log("BULK UPDATE RESPONSE:", response);
      return response;
    } catch (error) {
      console.error("Error updating customer preferences:", error);
      throw new Error(error instanceof Error ? error.message : "Error updating preferences");
    }
  };

  const getClients = async (businessId: number, params?: GetClientsParams) => {
    const additionalParams: AdditionalParams = {
      action: "getFiltered",
      ...(params?.limit && { limit: params.limit }),
      ...(params?.page && { page: params.page }),
      ...(params?.name && { name: params.name }),
      ...(params?.orderby && { orderby: params.orderby }),
      ...(params?.order && { order: params.order }),
      ...(params?.email && { email: params.email }),
      ...(params?.phone && { phone: params.phone }),
      ...(params?.source && { source: params.source }),
      ...(params?.customer_group && { customer_group: params.customer_group }),
      ...(params?.total_spent_min && { total_spent_min: params.total_spent_min }),
      ...(params?.total_spent_max && { total_spent_max: params.total_spent_max }),
      ...(params?.created_from && { created_from: params.created_from }),
      ...(params?.created_to && { created_to: params.created_to }),
      ...(params?.updated_from && { updated_from: params.updated_from }),
      ...(params?.updated_to && { updated_to: params.updated_to }),
      ...(params?.last_login_from && { last_login_from: params.last_login_from }),
      ...(params?.last_login_to && { last_login_to: params.last_login_to }),
      ...(params?.tag && { tag: params.tag }),
    };

    const response = await QlickUpAPI(
      businessId.toString(),
      "getFiltered",
      null,
      "clients",
      QlickupActions.GET,
      additionalParams
    );

    if (response) {
      return response as ClientsResponse;
    } else {
      return null;
    }
  };

  const AddClient = async (businessId: number, clientData) => {
    const response = await QlickUpAPI(
      businessId.toString(),
      "addUpdateCustomers",
      clientData,
      "clients",
      QlickupActions.POST
    );

    console.log(response);
  };

  const getClientNotes = async (businessId: number, clientId: string) => {
    try {
      const response = await QlickUpAPI(
        businessId.toString(),
        "getClientNotes",
        null,
        "clients",
        QlickupActions.GET,
        { clientId }
      );

      console.log("RESPONSE CLIENT NOTES", response);

      if (response) {
        return response as Note[];
      } else {
        return null;
      }
    } catch (error) {
      console.error("Error fetching client notes:", error);
      return null;
    }
  };

  const addClientNote = async (businessId: number, clientId: string, note: Note) => {
    try {
      const response = await QlickUpAPI(
        businessId.toString(),
        "addClientNote",
        note,
        "clients",
        QlickupActions.POST,
        { clientId }
      );

      console.log("RESPONSE ADD CLIENT NOTE", response);

      if (response) {
        return response.id as string;
      } else {
        return null;
      }
    } catch (error) {
      console.error("Error adding client note:", error);
      return null;
    }
  };

  const editClientNotes = async (businessId: number, clientId: string, note: Note) => {
    try {
      const response = await QlickUpAPI(
        businessId.toString(),
        "editClientNote",
        note,
        "clients",
        QlickupActions.POST,
        { clientId }
      );

      console.log("RESPONSE EDIT CLIENT NOTE", response);

      if (response) {
        return response as Note[];
      } else {
        return null;
      }
    } catch (error) {
      console.error("Error editing client note:", error);
      return null;
    }
  };

  const deleteClientNote = async (businessId: number, clientId: string, noteId: string) => {
    try {
      const response = await QlickUpAPI(
        businessId.toString(),
        "deleteClientNote",
        null,
        "clients",
        QlickupActions.POST,
        { clientId, noteId }
      );

      console.log("RESPONSE DELETE CLIENT NOTE", response);

      if (response) {
        return response as Note[];
      } else {
        return null;
      }
    } catch (error) {
      console.error("Error deleting client note:", error);
      return null;
    }
  };

  const searchClient = async (businessId: number, searchTerm: string) => {
    const response = await QlickUpAPI(
      businessId.toString(),
      "searchClient",
      null,
      "clients",
      QlickupActions.GET,
      { search: searchTerm }
    );

    if (response && response.success && Array.isArray(response.clients)) {
      // Create a proper Record<number, string> object
      const simplifiedRecords: Record<number, string> = {};

      // Fill the record with client data
      response.clients.forEach((client) => {
        simplifiedRecords[client.id] =
          client.full_name || `${client.name || ""} ${client.surname || ""}`.trim();
      });

      return {
        ...response,
        simplifiedRecords,
      };
    } else {
      return null;
    }
  };

  return {
    getClients,
    addSingleClient: AddClient,
    getClientNotes,
    addClientNote,
    searchClient,
    editClientNotes,
    deleteClientNote,
    bulkUpdateCustomerColumns,
  };
};
